import type { ReactNode } from "react"

import { BottomSheet, Button, Heading, buttonColors, buttonSizes, buttonTypes } from "../.."
import { AddIcon, CheckIcon, EllipsisIcon, ExportIcon } from "../../../assets/icons"

import type { UsePwaInstallResult } from "./usePwaInstall"

import styles from "./pwaInstallSheet.module.css"

export type PwaInstallSheetLiterals = {
  installQuestion: string
  tapToInstall: string
  incompatibleBrowserTitle: string
  incompatibleBrowserMessage: ReactNode
  incompatibleBrowserCopyLink: string
  incompatibleBrowserLinkCopied: string
  iosStep1: string
  iosStep2: string
  iosStep3: string
  shareText?: string
  moreText?: string
  addToHomeScreenText: string
}

export type PwaInstallSheetProps = {
  pwa: UsePwaInstallResult
  literals: PwaInstallSheetLiterals
  appName: string
  appUrl: string
  appLogo?: string
}

export const PwaInstallSheet = ({ pwa, literals, appName, appUrl, appLogo }: PwaInstallSheetProps) => {
  const renderIosStep = (text: string) => {
    const parts = text.split(/(<ellipsisIcon\s*\/>|<shareButton\s*\/>|<moreButton\s*\/>|<addToHomeScreen\s*\/>)/)
    return (
      <p className={styles.stepText}>
        {parts.map((part, index) => {
          if (part.match(/<ellipsisIcon\s*\/>/)) {
            return (
              <span key={index} className={styles.iconBadge}>
                <EllipsisIcon />
              </span>
            )
          }
          if (part.match(/<shareButton\s*\/>/)) {
            return (
              <span key={index} className={styles.iconBadge} aria-label={literals.shareText || "Share"}>
                <ExportIcon />
              </span>
            )
          }
          if (part.match(/<moreButton\s*\/>/)) {
            return (
              <span key={index} className={styles.iconBadge} aria-label={literals.moreText || "More"}>
                <EllipsisIcon />
              </span>
            )
          }
          if (part.match(/<addToHomeScreen\s*\/>/)) {
            return (
              <span key={index} className={styles.textBadge}>
                <span className={styles.badgeIcon}>
                  <AddIcon />
                </span>
                <span>{literals.addToHomeScreenText}</span>
              </span>
            )
          }
          return <span key={index}>{part}</span>
        })}
      </p>
    )
  }

  const renderIncompatibleTitle = () => {
    const parts = literals.incompatibleBrowserTitle.split(/(<br\s*\/>)/)
    return (
      <Heading as="h3">
        {parts.map((part, index) => {
          if (part === "<br />") return <br key={index} />
          return <span key={index}>{part}</span>
        })}
      </Heading>
    )
  }

  const instructionsTitle = pwa.isIncompatibleBrowser ? undefined : literals.installQuestion

  return (
    <BottomSheet isOpen={pwa.showInstructions} onClose={pwa.closeInstructions} title={instructionsTitle}>
      {pwa.isIncompatibleBrowser && <div className={styles.incompatibleHeader}>{renderIncompatibleTitle()}</div>}

      {appLogo && (
        <div className={styles.appCard}>
          <div className={styles.appIconContainer}>
            <img src={appLogo} alt={appName} className={styles.appLogo} />
          </div>
          <div className={styles.appInfo}>
            <h3 className={styles.appName}>{appName}</h3>
            <p className={styles.appUrl}>{appUrl}</p>
          </div>
        </div>
      )}

      {pwa.isIncompatibleBrowser ? (
        <>
          <p className={styles.incompatibleMessage}>{literals.incompatibleBrowserMessage}</p>
          {pwa.linkCopied ? (
            <div className={styles.copiedMessage}>
              <span className={styles.checkIcon}>
                <CheckIcon />
              </span>
              <Heading as="h3" color="success" align="center">
                {literals.incompatibleBrowserLinkCopied}
              </Heading>
            </div>
          ) : (
            <Button
              onClick={pwa.copyLink}
              type={buttonTypes.FILLED}
              color={buttonColors.PRIMARY}
              size={buttonSizes.MEDIUM}
              className={styles.copyLinkButton}
            >
              {literals.incompatibleBrowserCopyLink}
            </Button>
          )}
        </>
      ) : pwa.isAndroidInstallable ? (
        <>
          {pwa.errorMessage && (
            <div className={styles.errorMessage}>
              <p className={styles.errorMessageText}>{pwa.errorMessage}</p>
            </div>
          )}
          <Button
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              void pwa.nativeInstall()
            }}
            type={buttonTypes.FILLED}
            color={buttonColors.PRIMARY}
            size={buttonSizes.MEDIUM}
            isFull
          >
            {literals.tapToInstall}
          </Button>
        </>
      ) : (
        <div className={styles.stepsContainer}>
          <div className={styles.step}>
            <span className={styles.stepNumber}>1.</span>
            <div className={styles.stepContent}>{renderIosStep(literals.iosStep1)}</div>
          </div>
          <div className={styles.step}>
            <span className={styles.stepNumber}>2.</span>
            <div className={styles.stepContent}>{renderIosStep(literals.iosStep2)}</div>
          </div>
          <div className={styles.step}>
            <span className={styles.stepNumber}>3.</span>
            <div className={styles.stepContent}>{renderIosStep(literals.iosStep3)}</div>
          </div>
        </div>
      )}
    </BottomSheet>
  )
}
